import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { cropPublicId } from '../../utils/cropPublicId';
import { useTranslation } from '../../utils/useTranslation';
import { Skeleton } from '../feedback/Skeleton';
import { Button } from '../input/Button';
import { Box } from '../layout/Box';
import { Flex, FlexProps } from '../layout/Flex';
import { Avatar } from '../media/Avatar';
import { Typography } from '../typography/Typography';
import type { BusinessUI } from './types';

export const calculateHeight = () => 60;

const Root = styled(Flex).attrs({
    py: 1.5,
    direction: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
})`
    border-bottom: 1px solid var(--divider);

    &:last-child {
        border-bottom-color: transparent;
    }
`;

interface BusinessListItemProps extends FlexProps {
    business: BusinessUI;
    onSubscribe: (id: BusinessUI) => void;
}

export const BusinessListItem = (props: BusinessListItemProps) => {
    const { business, ...other } = props;
    const t = useTranslation();

    const onSubscribe = useCallback(() => {
        props.onSubscribe(business);
    }, [business, props.onSubscribe]);

    return (
        <Root {...other}>
            <Flex
                as={Link}
                to={business.url}
                direction="row"
                alignItems="center"
                textDecoration="none"
                spacing={1}
            >
                <Avatar title={business.displayName} src={business.avatarUrl} />
                <Box>
                    <Typography as="h3" variant="caption" weight="bold">
                        {cropPublicId(business.publicId)}
                    </Typography>
                    <Typography as="span" variant="caption" weight="medium" color="secondary">
                        {business.displayName}
                    </Typography>
                </Box>
            </Flex>
            <Button size="s" variant="outlined" onClick={onSubscribe}>
                {business.subscribed ? t('Вы подписаны') : t('Подписаться')}
            </Button>
        </Root>
    );
};

export const BusinessListItemSkeleton = (props: FlexProps) => (
    <Root {...props}>
        <Flex direction="row" alignItems="center" textDecoration="none" spacing={1}>
            <Skeleton variant="circular" width={32} height={32} />
            <Box>
                <Skeleton variant="text" width={150} height={14} />
                <Skeleton variant="text" width={120} height={14} />
            </Box>
        </Flex>
        <Skeleton variant="rect" width={120} height={34} borderRadius="999px" />
    </Root>
);
